import type {
  CustomerInfoData,
  CustomerUsageTotals,
  CustomerAllData,
  ResourceMetricData,
  ResourceMetricType,
} from '@/lib/types/Customer';
import type { IAuthenticatedCPUser } from '@/lib/types/Auth';

const BYTES_PER_GB = 1073741824; // 1024^3

export interface AppLicenseInfo {
  /** Master account's total app license limit (MAX_SAFE_INTEGER if unlimited). */
  masterAppLimit: number;
  /** Apps currently in use by the master account. */
  masterAppsInUse: number;
  /** Apps currently in use by the specified sub-account. */
  subAccountAppsInUse: number;
  /** Total apps in use (master + specified sub-account). */
  totalAppsInUse: number;
  /** Sum of app limits allocated to other sub-accounts (excluding the specified one). */
  otherSubsAllocatedLimits: number;
  /** Remaining app licenses available for assignment: masterAppLimit - totalAppsInUse. */
  appsAvailableForAssignment: number;
}

export class AccountUtils {
  /**
   * Resolve the parent (master) cId for billing API calls.
   * Billing always uses the master account cId, never a sub-account cId.
   */
  static resolveParentCId(
    customerInfo: CustomerInfoData | null,
    authData: IAuthenticatedCPUser | null,
  ): number | null {
    if (!customerInfo || !authData) return null;

    const userCId = authData.cpuser.core.cId;
    const masterCId = customerInfo.customer.cfg.cId;
    const isFromParentAccount = userCId === masterCId;

    if (isFromParentAccount) {
      return masterCId;
    }

    // User is from a sub-account — use parentCId from the master customer config
    // Use || instead of ?? because backend may return 0 instead of null for "no parent"
    return customerInfo.customer.cfg.parentCId || masterCId;
  }

  /**
   * Check if the logged-in user belongs to the parent/master account.
   */
  static isFromParentAccount(
    authData: IAuthenticatedCPUser | null,
    customerInfo: CustomerInfoData | null,
  ): boolean {
    if (!authData || !customerInfo) return false;
    return authData.cpuser.core.cId === customerInfo.customer.cfg.cId;
  }

  /** Convert bytes to GB for display. Clamps negative values to 0. */
  static bytesToGB(bytes: number): number {
    if (!isFinite(bytes) || bytes < 0) return 0;
    return bytes / BYTES_PER_GB;
  }

  /** Convert GB input to bytes for API transmission. */
  static gbToBytes(gb: number): number {
    return Math.round(gb * BYTES_PER_GB);
  }

  /** Format bytes as a human-readable GB string (e.g. "146.2 GB"). */
  static formatBytesAsGB(bytes: number, decimals = 1): string {
    const gb = AccountUtils.bytesToGB(bytes);
    return `${gb.toFixed(decimals)} GB`;
  }

  /** Calculate usage percentage (0-100). Returns null if no limit or invalid input. */
  static usagePercent(used: number, limit: number | null): number | null {
    if (limit === null || limit === 0) return null;
    if (!isFinite(used) || used < 0) return 0;
    return Math.min(100, (used / limit) * 100);
  }

  /**
   * Get progress bar color based on usage percentage.
   * Green: under 75%, Yellow: 75-100% (approaching), Red: over 100% (exceeded).
   */
  static usageColor(percent: number): string {
    if (percent > 100) return 'red';
    if (percent >= 75) return 'yellow';
    return 'green';
  }

  /**
   * Sum the CURRENT-month resource-metric value (bytes) of a given type across
   * the supplied metrics. The backend leaves cfg.quota.limits.{storage,transfer}
   * BytesUsed at 0 and expects the dashboard to derive live usage from the
   * resourceMetrics buckets (parity with legacy
   * ResourceMetricsUtil.getCurrentMonth{Storage,DataTransfer}Bytes). The current
   * bucket is the month interval containing `nowSeconds` (start inclusive, end
   * exclusive). `nowSeconds` is injected for deterministic testing.
   */
  static currentMonthResourceUsage(
    metrics: ResourceMetricData[],
    type: ResourceMetricType,
    nowSeconds: number,
  ): number {
    let total = 0;
    for (const m of metrics) {
      if (
        m.resourceMetricType === type &&
        m.resourceMetricInterval === 'month' &&
        m.start <= nowSeconds &&
        m.end > nowSeconds
      ) {
        total += m.value;
      }
    }
    return total;
  }

  /**
   * Calculate app license info for a sub-account.
   * Reusable across sub-account form validation, grid display, and helper text.
   * Matches legacy Subaccount.component.tsx validateAppCfgLimit logic.
   */
  static getAppLicenseInfo(
    masterAccount: CustomerAllData,
    subAccounts: { account: CustomerAllData; appCfgsInUse: number }[],
    usageTotals: CustomerUsageTotals[],
    currentSubCId: number | null,
  ): AppLicenseInfo {
    const masterAppLimit = masterAccount.cfg.quota.features.app.appCfgLimit ?? Number.MAX_SAFE_INTEGER;

    const masterAppsInUse = usageTotals.find(t => t.cId === masterAccount.cfg.cId)?.appCfgs ?? 0;

    const currentSub = currentSubCId
      ? subAccounts.find(s => s.account.cfg.cId === currentSubCId)
      : null;
    const subAccountAppsInUse = currentSub?.appCfgsInUse ?? 0;

    const totalAppsInUse = masterAppsInUse + subAccountAppsInUse;

    const otherSubsAllocatedLimits = subAccounts
      .filter(s => s.account.cfg.cId !== currentSubCId)
      .reduce((sum, s) => sum + (s.account.cfg.quota.features.app.appCfgLimit ?? 0), 0);

    const appsAvailableForAssignment = masterAppLimit - totalAppsInUse;

    return {
      masterAppLimit,
      masterAppsInUse,
      subAccountAppsInUse,
      totalAppsInUse,
      otherSubsAllocatedLimits,
      appsAvailableForAssignment,
    };
  }
}
